import { useCallback, useEffect, useMemo, useState, Suspense } from 'react';
import { Outlet } from '@tanstack/react-router';

import { useSidebarState } from '../../../shared/hooks/use-sidebar-state';
import { WorkspaceShellProvider, type WorkspaceShellMode } from '../hooks/use-workspace-shell';
import { WorkspaceSidebar } from './workspace-sidebar';

import { PageLoader } from '@/shared/components/loader/page-loader';
import { MotionDiv } from '@/shared/components/motion-div';
import { SidebarInset, SidebarProvider } from '@/shared/components/shadcn/sidebar';

export const WorkspaceLayout = ({ workspaceId }: { workspaceId: string }) => {
  const { isCollapsed, setCollapsed } = useSidebarState();
  const [shellMode, setShellMode] = useState<WorkspaceShellMode>('default');
  const [focusSidebarOpen, setFocusSidebarOpen] = useState(false);

  useEffect(() => {
    if (shellMode === 'discovery-focus') {
      setFocusSidebarOpen(false);
    }
  }, [shellMode]);

  const handleShellModeChange = useCallback((nextMode: WorkspaceShellMode) => {
    setShellMode((prevMode) => (prevMode === nextMode ? prevMode : nextMode));
  }, []);

  const handleSidebarOpenChange = useCallback(
    (open: boolean) => {
      if (shellMode === 'discovery-focus') {
        setFocusSidebarOpen(open);
        return;
      }

      setCollapsed(!open);
    },
    [setCollapsed, shellMode],
  );

  const isSidebarOpen = shellMode === 'discovery-focus' ? focusSidebarOpen : !isCollapsed;

  const shellValue = useMemo(
    () => ({
      mode: shellMode,
      setMode: handleShellModeChange,
      isSidebarOpen,
      setSidebarOpen: handleSidebarOpenChange,
      showSidebar: () => handleSidebarOpenChange(true),
    }),
    [handleShellModeChange, handleSidebarOpenChange, isSidebarOpen, shellMode],
  );

  return (
    <WorkspaceShellProvider value={shellValue}>
      <MotionDiv>
        <Suspense fallback={<PageLoader className="h-screen" />}>
          <SidebarProvider open={isSidebarOpen} onOpenChange={handleSidebarOpenChange}>
            <WorkspaceSidebar workspaceId={workspaceId} />
            <SidebarInset className="relative flex h-screen flex-1 flex-col gap-2 overflow-auto">
              <Suspense fallback={<PageLoader />}>
                <Outlet />
              </Suspense>
            </SidebarInset>
          </SidebarProvider>
        </Suspense>
      </MotionDiv>
    </WorkspaceShellProvider>
  );
};
